import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { BrandLockup } from "@/components/layout/BrandLockup";
import { useAuthActions } from "@/lib/auth/useAuth";
import { toast } from "@/components/Toast/useToast";
import { SurveyModal } from "@/components/SurveyModal";

const SURVEY_MODE = import.meta.env.VITE_SURVEY_MODE === "true";

// ─── Supabase error → human-readable message ─────────────────────────────────
function mapLoginError(raw: string): {
  field: "email" | "password" | "form";
  message: string;
} {
  const msg = raw.toLowerCase();
  if (
    msg.includes("invalid login credentials") ||
    msg.includes("invalid credentials")
  ) {
    return {
      field: "form",
      message: "Email or password is incorrect. Please try again.",
    };
  }
  if (msg.includes("email not confirmed")) {
    return {
      field: "email",
      message: "Please confirm your email address before signing in.",
    };
  }
  if (
    msg.includes("user not found") ||
    msg.includes("no user") ||
    msg.includes("wrong password") ||
    msg.includes("incorrect password") ||
    msg.includes("invalid login credentials")
  ) {
    return { field: "form", message: "Invalid email or password." };
  }
  if (msg.includes("too many requests") || msg.includes("rate limit")) {
    return {
      field: "form",
      message: "Too many attempts. Please wait a moment and try again.",
    };
  }
  return { field: "form", message: raw };
}

type FieldErrors = { email?: string; password?: string; form?: string };

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthActions();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);

  // ── Client-side validation before calling the server ─────────────────────
  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (!email.trim()) {
      errors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!password) {
      errors.password = "Password is required.";
    }
    return errors;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setIsLoading(true);

    const result = await login(email, password);
    setIsLoading(false);

    if (result.success) {
      navigate("/dashboard");
    } else {
      const mapped = mapLoginError(result.error ?? "Login failed");
      if (mapped.field === "form") {
        toast.error(mapped.message);
      } else {
        setFieldErrors({ [mapped.field]: mapped.message });
      }
    }
  };

  const fieldClass = (hasError: boolean) =>
    `w-full px-4 py-3 rounded-lg transition-colors outline-none ${
      hasError ? "ring-2 ring-[rgba(185,28,28,0.4)]" : ""
    }`;

  const fieldStyle = (hasError: boolean): React.CSSProperties => ({
    backgroundColor: "#ffffff",
    color: "#10201d",
    border: hasError ? "1px solid #b91c1c" : "1px solid #bdc9c4",
  });

  const fieldFocusHandler = (hasError: boolean) => ({
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.style.borderColor = hasError ? "#b91c1c" : "#006b5f";
      e.target.style.boxShadow = hasError
        ? "0 0 0 2px rgba(185,28,28,0.4)"
        : "0 0 0 2px rgba(0,107,95,0.35)";
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.style.borderColor = hasError ? "#b91c1c" : "#bdc9c4";
      e.target.style.boxShadow = "none";
    },
  });

  return (
    <main
      id="main-content"
      className="flex min-h-[100dvh] overflow-x-hidden overflow-y-auto"
      style={{ backgroundColor: "#f3f5f2" }}
    >
      {/* Left Side - Form */}
      <div className="flex min-w-0 flex-1 items-center justify-center px-4 py-6 sm:px-6 sm:py-12">
        <div className="max-w-md w-full">
          <div className="mb-8 sm:mb-12">
            <Link
              to="/"
              className="inline-block hover:opacity-80 transition-opacity"
            >
              <BrandLockup size="xl" />
            </Link>
          </div>

          <div className="mb-8">
            <h1
              className="text-3xl sm:text-4xl font-bold mb-2"
              style={{ color: "#10201d" }}
            >
              Welcome Back
            </h1>
            <p style={{ color: "#52645f" }}>Sign in to continue your training</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium mb-2"
                style={{ color: "#344844" }}
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email)
                    setFieldErrors((prev) => ({ ...prev, email: undefined }));
                }}
                className={fieldClass(!!fieldErrors.email)}
                style={{
                  ...fieldStyle(!!fieldErrors.email),
                  ...(isLoading ? { opacity: 0.6 } : {}),
                }}
                placeholder="you@example.com"
                disabled={isLoading}
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? "login-email-error" : undefined}
                {...fieldFocusHandler(!!fieldErrors.email)}
              />
              {fieldErrors.email && (
                <p
                  id="login-email-error"
                  role="alert"
                  className="mt-1.5 text-xs flex items-center gap-1"
                  style={{ color: "#b91c1c" }}
                >
                  <svg
                    className="w-3 h-3 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {fieldErrors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium mb-2"
                style={{ color: "#344844" }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password)
                    setFieldErrors((prev) => ({
                      ...prev,
                      password: undefined,
                    }));
                }}
                className={fieldClass(!!fieldErrors.password)}
                style={{
                  ...fieldStyle(!!fieldErrors.password),
                  ...(isLoading ? { opacity: 0.6 } : {}),
                }}
                placeholder="••••••••"
                disabled={isLoading}
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? "login-password-error" : undefined}
                {...fieldFocusHandler(!!fieldErrors.password)}
              />
              {fieldErrors.password && (
                <p
                  id="login-password-error"
                  role="alert"
                  className="mt-1.5 text-xs flex items-center gap-1"
                  style={{ color: "#b91c1c" }}
                >
                  <svg
                    className="w-3 h-3 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {fieldErrors.password}
                </p>
              )}
              <div className="text-right mt-1.5">
                <Link
                  to="/forgot-password"
                  className="text-xs transition-colors hover:underline"
                  style={{ color: "#006b5f" }}
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {/* Form-level error (e.g. wrong credentials) */}
            {fieldErrors.form && (
              <div
                role="alert"
                className="p-3.5 rounded-lg text-sm flex items-start gap-2"
                style={{
                  backgroundColor: "rgba(185, 28, 28, 0.06)",
                  border: "1px solid rgba(185, 28, 28, 0.15)",
                  color: "#b91c1c",
                }}
              >
                <svg
                  className="w-4 h-4 mt-0.5 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                    clipRule="evenodd"
                  />
                </svg>
                {fieldErrors.form}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-3 font-bold rounded-lg transition-all"
              style={{
                backgroundColor: isLoading ? "#bdc9c4" : "#006b5f",
                color: isLoading ? "#8a9b95" : "#ffffff",
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = "#00574e";
              }}
              onMouseLeave={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = "#006b5f";
              }}
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p
            className="mt-6 text-center text-sm"
            style={{ color: "#52645f" }}
          >
            Don't have an account?{" "}
            {SURVEY_MODE ? (
              <button
                onClick={() => setSurveyOpen(true)}
                className="hover:underline font-medium"
                style={{ color: "#006b5f", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
              >
                Sign up
              </button>
            ) : (
              <Link
                to="/signup"
                className="hover:underline font-medium"
                style={{ color: "#006b5f" }}
              >
                Sign up
              </Link>
            )}
          </p>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:block flex-1 relative">
        <div
          className="absolute inset-0 z-10"
          style={{
            background: "linear-gradient(to bottom right, rgba(16,32,29,0.55), rgba(16,32,29,0.55))",
          }}
        ></div>
        <img
          src="https://images.unsplash.com/photo-1758957097813-9b391db18caa?w=1200&q=80"
          alt="Cyclist training"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-12 left-12 right-12 z-20">
          <h2
            className="text-3xl font-bold mb-2"
            style={{ color: "#ffffff" }}
          >
            Train with Purpose
          </h2>
          <p
            className="text-lg"
            style={{ color: "rgba(255,255,255,0.75)" }}
          >
            Structured workouts based on your FTP and training goals
          </p>
        </div>
      </div>
      {surveyOpen && <SurveyModal onClose={() => setSurveyOpen(false)} />}
    </main>
  );
}
